import { faPuzzlePiece, faSliders } from "@fortawesome/free-solid-svg-icons";
import { computed } from "vue";
import { usePropertyBuilder } from "../components/views/config/helpers";
import type { IUseMods } from "../components/views/mods";
import { useSteamWorkshop } from "../components/views/mods/steam_workshop";
import type { ConanProfileNamespace, SteamWorkshopItem } from "../graphql";
import { i18n } from "../i18n";
import { useServiceStore } from "../store/service";
import { commonMenuItems } from "./common";
import defaultProfile, { asyncRoute } from "./default";
import type { ConfigSection, GameProfile, Menu, ProfileCtx } from "./types";
import { MenuItem } from "./types";

const { t } = i18n.global;

const UE5_REPO_KEY = "conanExilesUE5";

function buildConfigSections(ctx: ProfileCtx): ConfigSection[] {
  const sections: ConfigSection[] = [
    { component: "ConanBranchSection", props: { name: "branch" } },
    { component: "ConfSection", props: { name: "basic" } },
    {
      component: "TableSection",
      props: {
        name: "steamIDWhitelist",
        visibleIf: { config: "ServerSettings", field: "ServerSettings:EnableWhitelist", value: "True" },
      },
    },
    { component: "TableSection", props: { name: "broadcastMessages" } },
    { component: "ConanExilesTransfer", props: { name: "transfer" } },
  ];

  // repoKey is a service property, not a config value — it cannot change without a reload, so
  // filtering is still right for the transfer section.
  if (ctx.service?.config?.repoKey === UE5_REPO_KEY) {
    return sections.filter((section) => section.component !== "ConanExilesTransfer");
  }
  return sections;
}

const extendedSettingsMenuItem = new MenuItem(
  "extended-settings",
  faSliders,
  asyncRoute(() => import("../components/views/config/conan/ConanExtendedConf.vue")),
);

const createMenu = (): Menu => {
  const menu = defaultProfile().menu;
  // menu[0].items = [status, settings, fileManager] — insert extended settings after settings
  menu[0].items.splice(2, 0, extendedSettingsMenuItem);
  menu[0].items.push(
    commonMenuItems.logs,
    new MenuItem(
      "mods",
      faPuzzlePiece,
      asyncRoute(() => import("../components/views/config/conan/Mods.vue")),
      [
        {
          route: "manage",
          component: asyncRoute(() => import("../components/views/config/conan/ModsManage.vue")),
        },
      ],
    ),
  );
  return menu;
};

function useMods() {
  const service = useServiceStore();
  const { buildConfig } = usePropertyBuilder();

  const profile = computed(() => service.ctx.profile as ConanProfileNamespace | undefined);
  const selectedWorkshopItems = computed(() => profile.value?.selectedWorkshopItems ?? []);
  const subscribedFileIds = buildConfig<Array<string>>("subscribedFileIds", []);
  const subscribedCollectionIds = buildConfig<Array<string>>("subscribedCollectionIds", []);
  const modExtension: string = ".pak";

  return useSteamWorkshop(selectedWorkshopItems, subscribedFileIds, subscribedCollectionIds, modExtension, true);
}

const join = () => {
  window.open(t("ngp/settings/section/conan_wiki/link"), "_blank", "noopener,noreferrer");
};

export default (_ctx: ProfileCtx): GameProfile<SteamWorkshopItem, IUseMods<SteamWorkshopItem>> => ({
  menu: createMenu(),
  configSections: buildConfigSections(_ctx),
  join: {
    buttonTextKey: "ngp/settings/section/conan_wiki",
    joinFn: join,
  },
  useMods,
});
